// ---------------------------------------------------------------------------
// Range math and $-reference resolution
// ---------------------------------------------------------------------------

import type { Parameter, ParameterOption, ParameterRange, RangeBound } from '@shared/types';

/**
 * Calculates intuitive min and max values for a parameter based on its default value.
 * Creates ranges like: 94mm -> 0-100, 2.7 -> 0-10, 0.5 -> 0-1
 * Range is fixed based on default value to prevent runaway expansion.
 *
 * Note: RangeBound fields may be strings ($-references). This function treats any
 * string bound as undefined and falls back to the auto-heuristic, because
 * references should be resolved via resolveParameter() before this is called.
 */
export function calculateParameterRange(param: Parameter): {
  min: number;
  max: number;
} {
  // Treat string bounds (unresolved $-references) as absent — fall through to heuristic.
  const explicitMin =
    typeof param.range?.min === 'number' ? param.range.min : undefined;
  const explicitMax =
    typeof param.range?.max === 'number' ? param.range.max : undefined;

  // If explicit range is fully provided, use it directly
  if (explicitMin !== undefined && explicitMax !== undefined) {
    return { min: explicitMin, max: explicitMax };
  }

  const defaultValue = Number(param.defaultValue);
  if (isNaN(defaultValue)) {
    return { min: 0, max: 100 }; // Default fallback for non-number types
  }

  // Use only the default value for range calculation to prevent runaway expansion
  const referenceValue = Math.abs(defaultValue);

  // Handle zero or very small values
  if (referenceValue <= 0.001) {
    const min = explicitMin ?? 0;
    const max = explicitMax ?? 1;
    return { min, max };
  }

  // Calculate the order of magnitude
  const magnitude = Math.floor(Math.log10(referenceValue));
  const normalizedValue = referenceValue / Math.pow(10, magnitude);

  let rangeMultiplier: number;

  // Determine appropriate range based on normalized value
  // Round up to next nice number: 1, 2, 5, or 10
  if (normalizedValue <= 1) {
    rangeMultiplier = 1;
  } else if (normalizedValue <= 2) {
    rangeMultiplier = 2;
  } else if (normalizedValue <= 5) {
    rangeMultiplier = 5;
  } else {
    rangeMultiplier = 10;
  }

  let calculatedMax = rangeMultiplier * Math.pow(10, magnitude);

  // Special handling for values that should round up to next nice range
  // e.g., 94 -> 100, 2.7 -> 10 (since 2.7 is closer to 10 than 5)
  if (referenceValue > calculatedMax * 0.5) {
    if (rangeMultiplier === 1) {
      rangeMultiplier = 2;
    } else if (rangeMultiplier === 2) {
      rangeMultiplier = 5;
    } else if (rangeMultiplier === 5) {
      rangeMultiplier = 10;
    } else {
      rangeMultiplier = 10;
      calculatedMax = Math.pow(10, magnitude + 1);
    }
    calculatedMax = rangeMultiplier * Math.pow(10, magnitude);
  }

  // Determine final values, preferring explicit range settings
  const max = explicitMax ?? calculatedMax;

  let min: number;
  if (explicitMin !== undefined) {
    min = explicitMin;
  } else {
    // For negative default values, use symmetric range
    // For positive default values, start from 0
    if (defaultValue < 0) {
      min = -max;
    } else {
      min = 0;
    }
  }

  return { min, max };
}

/**
 * Calculates an appropriate step size for a parameter based on its range.
 */
export function calculateParameterStep(param: Parameter): number {
  // If explicit step is provided, use it.
  // Treat string bounds (unresolved $-references) as absent.
  const explicitStep =
    typeof param.range?.step === 'number' ? param.range.step : undefined;
  if (explicitStep !== undefined) {
    return explicitStep;
  }

  const { min, max } = calculateParameterRange(param);
  const range = max - min;

  // Handle very small ranges
  if (range <= 0.001) {
    return 0.001;
  }

  // Calculate step as 1% of the range, rounded to a nice number
  const rawStep = range / 100;
  const magnitude = Math.floor(Math.log10(rawStep));
  const normalizedStep = rawStep / Math.pow(10, magnitude);

  let stepMultiplier: number;
  if (normalizedStep <= 1) {
    stepMultiplier = 1;
  } else if (normalizedStep <= 2) {
    stepMultiplier = 2;
  } else if (normalizedStep <= 5) {
    stepMultiplier = 5;
  } else {
    stepMultiplier = 10;
  }

  return stepMultiplier * Math.pow(10, magnitude);
}

/**
 * Resolve a single RangeBound against live parameter values.
 * - Literal numbers are returned as-is.
 * - $-reference strings (e.g. "$height") look up the referenced parameter and
 *   coerce its current value to a number.
 * - Returns undefined when the reference cannot be resolved or is non-numeric.
 */
export function resolveRangeBound(
  bound: RangeBound | undefined,
  vars: Record<string, Parameter['value']>,
): number | undefined {
  if (bound === undefined) return undefined;
  if (typeof bound === 'number') return bound;
  // $-reference string
  if (bound.startsWith('$')) {
    const name = bound.slice(1);
    const val = vars[name];
    if (typeof val === 'number') return val;
    const coerced = Number(val);
    return isNaN(coerced) ? undefined : coerced;
  }
  return undefined;
}

/**
 * Resolve all bounds in a ParameterRange to concrete numbers.
 * Unresolvable $-references are replaced with undefined.
 */
export function resolveRange(
  range: ParameterRange | undefined,
  vars: Record<string, Parameter['value']>,
): ParameterRange | undefined {
  if (!range) return undefined;
  const resolved: ParameterRange = {};
  if (range.min !== undefined)
    resolved.min = resolveRangeBound(range.min, vars) ?? range.min;
  if (range.max !== undefined)
    resolved.max = resolveRangeBound(range.max, vars) ?? range.max;
  if (range.step !== undefined)
    resolved.step = resolveRangeBound(range.step, vars) ?? range.step;
  if (range.safeMin !== undefined)
    resolved.safeMin = resolveRangeBound(range.safeMin, vars) ?? range.safeMin;
  if (range.safeMax !== undefined)
    resolved.safeMax = resolveRangeBound(range.safeMax, vars) ?? range.safeMax;
  return resolved;
}

/**
 * Resolve a single ParameterOption whose value may be a $-reference string.
 * - If value is a $-ref and resolves to a number → {value: resolvedNum, label: String(resolvedNum)}
 * - If value is a $-ref and fails to resolve → {value: "?", label: "?"}
 * - Otherwise → option returned unchanged
 */
export function resolveOption(
  opt: ParameterOption,
  vars: Record<string, Parameter['value']>,
): ParameterOption {
  if (typeof opt.value === 'string' && opt.value.startsWith('$')) {
    const resolved = resolveRangeBound(opt.value, vars);
    if (resolved !== undefined) {
      return { value: resolved, label: String(resolved) };
    }
    return { value: '?', label: '?' };
  }
  return opt;
}

/** Returns true if the parameter has any $-reference in its range bounds or options. */
export function hasAnyReference(param: Parameter): boolean {
  const r = param.range;
  if (r) {
    for (const bound of [r.min, r.max, r.step, r.safeMin, r.safeMax]) {
      if (typeof bound === 'string' && bound.startsWith('$')) return true;
    }
  }
  if (param.options) {
    for (const opt of param.options) {
      if (typeof opt.value === 'string' && opt.value.startsWith('$'))
        return true;
    }
  }
  // Grid dimension $-references
  if (param.gridConfig) {
    if (typeof param.gridConfig.cols === 'string') return true;
    if (typeof param.gridConfig.rows === 'string') return true;
  }
  return false;
}

/**
 * Return a copy of param with all $-references resolved against live vars.
 * Fast-paths (returns the same object) when no references are present.
 */
export function resolveParameter(
  param: Parameter,
  vars: Record<string, Parameter['value']>,
): Parameter {
  if (!hasAnyReference(param)) return param;

  let resolvedGridConfig = param.gridConfig;
  if (param.gridConfig) {
    const resolveDim = (dim: number | string): number | string => {
      if (typeof dim === 'number') return dim;
      // $-reference: strip $ and look up in vars
      const name = dim.slice(1);
      const val = vars[name];
      if (typeof val === 'number') return Math.max(1, Math.round(val));
      const coerced = Number(val);
      return isNaN(coerced) ? dim : Math.max(1, Math.round(coerced));
    };
    resolvedGridConfig = {
      ...param.gridConfig,
      cols: resolveDim(param.gridConfig.cols),
      rows: resolveDim(param.gridConfig.rows),
    };
  }

  return {
    ...param,
    range: resolveRange(param.range, vars),
    options: param.options?.map((opt) => resolveOption(opt, vars)),
    gridConfig: resolvedGridConfig,
  };
}
